import React from "react";
import styled from "styled-components";
import { spacing } from "@magicbi/ui-library";
import { DayPill } from "../../atoms/DayPill";
import { WEEKDAYS } from "../../organisms/DeliverStory/types";
import type { Weekday } from "../../organisms/DeliverStory/types";
import {
  WEEKDAY_FULL,
  WEEKDAY_LABELS,
} from "../../../utils/scheduleConstants";
import {
  Field,
  FieldLabel,
  HelperText,
} from "../shared/scheduleField.styles";

export interface WeekdayPillRowProps {
  value: Weekday[];
  onToggle: (day: Weekday) => void;
  /**
   * `between` (default) spreads the pills edge to edge. `start` packs them left with a small gap,
   * for the inline schedule sentence.
   */
  layout?: "between" | "start";
  error?: string;
  showError?: boolean;
  className?: string;
}

/**
 * Seven 36px pills spread across the full width. `space-between` (not a fixed
 * gap) is what Figma's 56.67px stride actually is — it keeps the row flush to
 * both edges at the design width and closes the gaps up as the modal narrows.
 *
 * Deliberately no `gap`: the pills never shrink, so 7 x 36px = 252px is the
 * row's hard floor. Even a 4px gap raises that to 276px, and the last pill
 * then escapes the modal at a 320px viewport (measured, not guessed).
 */
const Row = styled.div<{ $layout: "between" | "start" }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $layout }) => ($layout === "start" ? "flex-start" : "space-between")};
  ${({ $layout }) => ($layout === "start" ? `gap: ${spacing[2]};` : "")}
  width: 100%;
`;

/** Weekly "Repeat on" — the day-of-week toggles. */
export const WeekdayPillRow: React.FC<WeekdayPillRowProps> = ({
  value,
  onToggle,
  layout = "between",
  error,
  showError = false,
  className,
}) => {
  const visibleError = showError && error ? error : undefined;

  return (
    <Field className={className}>
      <FieldLabel id="weekday-pill-row-label">Repeat on</FieldLabel>
      <Row $layout={layout} role="group" aria-labelledby="weekday-pill-row-label">
        {WEEKDAYS.map((day) => (
          <DayPill
            key={day}
            letter={WEEKDAY_LABELS[day]}
            ariaLabel={WEEKDAY_FULL[day]}
            selected={value.includes(day)}
            onClick={() => onToggle(day)}
            size="dayPill"
            compact
          />
        ))}
      </Row>
      {visibleError && <HelperText $error>{visibleError}</HelperText>}
    </Field>
  );
};

export default WeekdayPillRow;
